import React, { useState } from "react";
import axios from "axios";
import { Check, ExternalLink, Shield } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Full-screen blocking modal shown when an authenticated user has NOT yet
 * accepted the current Terms of Use + Privacy Policy version. Renders on
 * top of the dashboard until they tick the box and click Accept.
 *
 * Mounted by the AppLayout (every authenticated route). Self-fetches the
 * user's acceptance status on mount so it works regardless of which page
 * the user lands on first after sign-in.
 */
export default function AcceptTermsModal({ onAccepted }) {
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleAccept = async () => {
    if (!agreed) return;
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/legal/accept`, {}, { withCredentials: true });
      toast.success("Welcome aboard, heister.");
      onAccepted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't record acceptance");
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center px-4"
      data-testid="accept-terms-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
      <div className="relative max-w-xl w-full surface-glass border-2 border-[#FFCC00] p-6 md:p-8">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 flex items-center justify-center bg-[#FFCC00]/15 border border-[#FFCC00] text-[#FFCC00]">
            <Shield className="w-5 h-5" />
          </div>
          <div>
            <div className="label-kicker text-[#FFCC00]">One last thing</div>
            <div className="font-heading text-2xl">Agree to the rules</div>
          </div>
        </div>

        <p className="text-sm text-white/70 leading-relaxed mb-5">
          Before you start ripping through cities, we need you to agree to our{" "}
          <a
            href="/terms"
            target="_blank"
            rel="noreferrer"
            className="text-[#FFCC00] underline underline-offset-2 inline-flex items-center gap-1 hover:text-[#FFD633]"
            data-testid="modal-terms-link"
          >
            Terms of Use <ExternalLink className="w-3 h-3" />
          </a>{" "}
          and{" "}
          <a
            href="/privacy"
            target="_blank"
            rel="noreferrer"
            className="text-[#FFCC00] underline underline-offset-2 inline-flex items-center gap-1 hover:text-[#FFD633]"
            data-testid="modal-privacy-link"
          >
            Privacy Policy <ExternalLink className="w-3 h-3" />
          </a>
          . Skim them or read every word — your call.
        </p>

        <div className="border border-white/10 bg-black/40 px-4 py-3 mb-5 space-y-2 text-[11px] text-white/55">
          <div>
            <span className="text-white/80">By continuing, you confirm that</span>:
          </div>
          <ul className="list-disc pl-5 space-y-1">
            <li>You are 13 or older (or have a guardian's consent if 13–17).</li>
            <li>You agree to the Terms of Use, including the binding-arbitration clause in Section 17.</li>
            <li>You agree to our Privacy Policy.</li>
          </ul>
        </div>

        <label
          className="flex items-start gap-3 cursor-pointer select-none p-3 border border-white/10 hover:border-[#FFCC00] transition-colors"
          data-testid="agree-checkbox-label"
        >
          <span
            className={`w-5 h-5 mt-0.5 flex-shrink-0 border-2 flex items-center justify-center transition-colors ${
              agreed
                ? "bg-[#FFCC00] border-[#FFCC00]"
                : "bg-transparent border-white/40 hover:border-white/60"
            }`}
          >
            {agreed && <Check className="w-3.5 h-3.5 text-black" strokeWidth={3.5} />}
          </span>
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="sr-only"
            data-testid="agree-checkbox"
          />
          <span className="text-sm text-white/85 leading-snug">
            I have read and agree to the{" "}
            <span className="text-[#FFCC00]">Terms of Use</span> and{" "}
            <span className="text-[#FFCC00]">Privacy Policy</span>.
          </span>
        </label>

        <button
          onClick={handleAccept}
          disabled={!agreed || submitting}
          className={`mt-5 w-full py-3 font-heading text-sm uppercase tracking-[0.25em] transition-all ${
            agreed && !submitting
              ? "bg-[#FFCC00] text-black hover:bg-[#FFD633] anim-pulse-red"
              : "bg-white/5 text-white/30 cursor-not-allowed"
          }`}
          data-testid="agree-accept-btn"
        >
          {submitting ? "Saving…" : "Agree & continue"}
        </button>

        <div className="mt-3 text-[10px] tracking-[0.3em] uppercase text-white/30 text-center">
          You'll only need to do this once.
        </div>
      </div>
    </div>
  );
}
